import {ref} from 'vue'

// One call page per browser for the client app.
// 28 Oct 2026, session 179-319-524-820: an iPhone had the call open twice, the new SMS link and the old e-mail link
// that Safari reloaded in the background. Every client page asks for the camera as soon as it opens, iOS gives the
// camera to one page only, and the page in the call was left without a picture.
// The client page that starts first holds a Web Lock until its call is over. Another client page of the same browser
// does not start: it says that AIRe Link is open elsewhere and offers "Use here", which moves the call to it (the
// other page leaves the call and releases the camera and microphone, the same as closing it). Browsers without Web
// Locks (iOS before 15.4) and the assistant app work as before.
const LOCK = 'airelink-client-call'
const TAKE_OVER = 'airelink:callTab:takeOver'
const MOVED = 'airelink:callTab:moved'

const readFlag = name => {
  try {
    const value = sessionStorage.getItem(name) === '1'
    sessionStorage.removeItem(name)
    return value
  } catch (error) {
    return false // site storage blocked
  }
}
const setFlag = name => {
  try {
    sessionStorage.setItem(name, '1')
  } catch (error) {
    // site storage blocked: the reloaded page asks for the lock again
  }
}

const enabled = import.meta.env.VITE_APP_TYPE === 'client' && typeof navigator !== 'undefined' && !!navigator.locks?.request
// 'checking' | 'active' (this page has the call) | 'elsewhere' (another page has it, this one did not start)
// | 'moved' (another page took it over, this one left the call)
const state = ref(enabled ? 'checking' : 'active')
let release = null
let onMoved = null
let taking = false

const hold = options =>
  new Promise((resolve, reject) => {
    navigator.locks
      .request(LOCK, options, lock => {
        if (!lock) return resolve(false)
        resolve(true)
        // Held until release(): when the call is over, or when the page closes
        return new Promise(done => (release = done))
      })
      .catch(error => {
        // Taken over by another page's "Use here" (AbortError), or the lock could not be requested at all
        if (state.value === 'active' && release) {
          release = null
          state.value = 'moved'
          onMoved?.()
        } else {
          reject(error)
        }
      })
  })

export default function useCallTab() {
  // Resolves true when this page may start the call. moved: called when another page takes the call over
  const start = async moved => {
    onMoved = moved
    if (!enabled) return true
    // Reloaded after the call moved to another page (reloadAsMoved): stay quiet
    if (readFlag(MOVED)) {
      state.value = 'moved'
      return false
    }
    const takeOver = readFlag(TAKE_OVER)
    try {
      state.value = (await hold(takeOver ? {steal: true} : {ifAvailable: true})) ? 'active' : 'elsewhere'
    } catch (error) {
      // Never keep the call from starting because of the lock
      console.error('[callTab] The lock could not be requested', error)
      state.value = 'active'
    }
    return state.value === 'active'
  }

  // "Use here" on a page that did not start: take the call over now. Resolves true when the page may start.
  // A second tap while the first is under way does nothing: its steal would take the lock from the first one
  const takeOver = async () => {
    if (taking) return false
    taking = true
    try {
      state.value = (await hold({steal: true})) ? 'active' : 'elsewhere'
    } catch (error) {
      console.error('[callTab] The call could not be taken over', error)
      state.value = 'active'
    } finally {
      taking = false
    }
    return state.value === 'active'
  }

  // "Use here" on a page that left the call for another one: start again (the reloaded page takes the call over)
  const restart = () => {
    setFlag(TAKE_OVER)
    window.location.reload()
  }

  // A page that was not in the call yet when another page took it over: a reload is the only sure way to drop a
  // camera request that is still pending (iOS answers it when the page comes to the front, and the other page would
  // lose the camera). The reloaded page shows the notice and does not start
  const reloadAsMoved = () => {
    setFlag(MOVED)
    window.location.reload()
  }

  // The call is over on this page: other client pages may start
  const end = () => {
    release?.()
    release = null
  }

  return {state, start, takeOver, restart, reloadAsMoved, end}
}
